'use client';

import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import styles from './CategoryFilterChips.module.css';
import type { StudyCategorySlug } from 'content/science/studyTypes';

/* Category filter chip group.
 *
 * Shows `All Studies` + every category that actually has items in
 * the current dataset, so empty categories don't render as ghost
 * chips.
 *
 * Visual: integratedbio `.group_option` — small rounded-rect
 * pill, 8px radius, padding 8/10, mono caps 12px. Active state
 * is solid black + white text. Variant `dark` inverts for use
 * on the snapshot section's dark surface. */

interface Option {
  slug: StudyCategorySlug | 'all';
  label: string;
  count: number;
}

interface Props {
  active: StudyCategorySlug | 'all';
  options: Option[];
  onChange: (slug: StudyCategorySlug | 'all') => void;
  variant?: 'light' | 'dark';
}

const CategoryFilterChips = ({
  active,
  options,
  onChange,
  variant = 'light',
}: Props) => {
  const t = useTranslations('science');
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);

  /* Roving tabindex: the group is one tab stop and Left/Right move between
     chips, which is what a tablist is expected to do. Without it every
     category is its own stop and a keyboard user tabs through all of them to
     reach the grid. */
  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    const last = options.length - 1;
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (e.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    onChange(options[next].slug);
    chipRefs.current[next]?.focus();
  };

  return (
    <div
      className={styles.group}
      role="tablist"
      aria-label={t('science.filter.byCategory')}
    >
      {options.map((opt, i) => (
        <button
          key={opt.slug}
          ref={(el) => {
            chipRefs.current[i] = el;
          }}
          type="button"
          role="tab"
          aria-selected={opt.slug === active}
          tabIndex={opt.slug === active ? 0 : -1}
          className={styles.chip}
          data-active={opt.slug === active ? 'true' : 'false'}
          data-variant={variant}
          onClick={() => onChange(opt.slug)}
          onKeyDown={(e) => onKeyDown(e, i)}
        >
          {opt.slug === 'all' ? t('science.filter.allStudies') : opt.label}
        </button>
      ))}
    </div>
  );
};

export default CategoryFilterChips;

/* Build the option list from a study array. "All Studies" first,
   then one chip per category that has at least one matching item,
   ordered by item count desc so the busiest categories surface
   first. */
export function buildFilterOptions(
  studies: { category: StudyCategorySlug; categoryLabel: string }[],
): Option[] {
  const counts = new Map<StudyCategorySlug, { label: string; count: number }>();
  for (const s of studies) {
    const cur = counts.get(s.category);
    if (cur) cur.count += 1;
    else counts.set(s.category, { label: s.categoryLabel, count: 1 });
  }
  const byCount = Array.from(counts.entries())
    .map(([slug, v]) => ({ slug, label: v.label, count: v.count }))
    .sort((a, b) => b.count - a.count);

  return [
    { slug: 'all', label: 'All Studies', count: studies.length },
    ...byCount,
  ];
}
